import { ConnectProto } from "@racoon/api";
import { fromTimestamp, startOfLocalDay } from "@racoon/lib";

type Id = bigint | number | string;

type DepotGroup = {
  ownerOrganizationId?: Id;
  serviceProviders?: { id: Id }[];
  contract?: {
    serviceProviderId?: Id;
    targetOrganizationId?: Id;
    status?: ConnectProto.ContractStatus;
    endAt?: Parameters<typeof fromTimestamp>[0];
  };
};

type DepotLocation = { serviceProviderId?: Id; ownerOrganizationId?: Id };

const same = (a: Id | undefined, b: Id | undefined) => String(a ?? 0) === String(b ?? 0);

// Live as the backend reads it (Contract.IsLive): active or hidden, through its end day.
const isLive = (contract: NonNullable<DepotGroup["contract"]>, now: Date) => {
  const live =
    contract.status === undefined ||
    contract.status === ConnectProto.ContractStatus.ACTIVE ||
    contract.status === ConnectProto.ContractStatus.HIDDEN;
  const endAt = fromTimestamp(contract.endAt);
  return live && (!endAt || endAt >= startOfLocalDay(now));
};

// A new job's depot, as the backend's NewJobExecutor picks it: the zone's first provider (while its
// contract is live), else the provider serving the group (its live contract's, else its own site's),
// else the creating company.
// Shown only: CreateJob resolves the executor itself.
export const newJobDepotId = (
  group: DepotGroup,
  location: DepotLocation | undefined,
  companyId: Id | null | undefined,
  now = new Date(),
): string | undefined => {
  const contract = group.contract;
  // The zone was checked under its contract's provider; once that contract ends, the backend drops
  // divisions outside the new serving provider, which only it can tell.
  const zoneFirst =
    !contract || isLive(contract, now) ? group.serviceProviders?.[0]?.id : undefined;
  if (zoneFirst) return String(zoneFirst);
  if (
    contract?.serviceProviderId &&
    isLive(contract, now) &&
    same(contract.targetOrganizationId, group.ownerOrganizationId)
  )
    return String(contract.serviceProviderId);
  if (location?.serviceProviderId && same(location.ownerOrganizationId, group.ownerOrganizationId))
    return String(location.serviceProviderId);
  return companyId ? String(companyId) : undefined;
};
